import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useStripe } from '@stripe/react-stripe-js'

import { stopFetching } from 'root-redux/actions/common'
import { selectUserCountryCode } from 'root-redux/selects/user'

import { captureFunnelError } from 'helpers/captureFunnelError'
import { FunnelErrorCode } from 'helpers/funnelErrors'

import { getPaymentRequestPaymentMethod } from 'modules/purchase/helpers/getPaymentRequestPaymentMethod'
import { isWalletAvailableByCountry } from 'modules/purchase/helpers/isWalletAvailableByCountry'
import { usePurchaseStore } from 'modules/purchase/hooks/usePurchaseStore'
import {
  CHECK_PAYMENT_REQUEST_BUTTON,
  setAvailableAlternativePaymentMethods,
} from 'modules/purchase/redux/actions/common'
import { selectAvailablePaymentMethods } from 'modules/purchase/redux/selects/common'

import { CENTS_IN_DOLLAR } from 'root-constants'

const WALLET_DETECTION_LABEL = 'Subscription'

export const useCheckAppleGooglePayAvailability = () => {
  const stripe = useStripe()
  const dispatch = useDispatch()
  const countryCode = useSelector(selectUserCountryCode)
  const availableAlternativePaymentMethods = useSelector(
    selectAvailablePaymentMethods,
  )
  const { trialPrice, price, currency } = usePurchaseStore()

  const hasCheckedRef = useRef(false)

  const calculatedPrice = useMemo(
    () => +((trialPrice || price) * CENTS_IN_DOLLAR).toFixed(),
    [trialPrice, price],
  )

  useEffect(() => {
    if (
      !stripe ||
      hasCheckedRef.current ||
      !currency ||
      !calculatedPrice ||
      availableAlternativePaymentMethods.length
    ) {
      return
    }

    hasCheckedRef.current = true

    const paymentRequest = stripe.paymentRequest({
      currency,
      country: 'GB',
      requestPayerEmail: true,
      requestPayerName: true,
      total: {
        label: WALLET_DETECTION_LABEL,
        amount: calculatedPrice,
      },
    })

    paymentRequest
      .canMakePayment()
      .then((result) => {
        const paymentMethod = getPaymentRequestPaymentMethod(result)

        if (!paymentMethod) {
          return
        }

        if (!isWalletAvailableByCountry(paymentMethod, countryCode)) {
          return
        }

        dispatch(setAvailableAlternativePaymentMethods(paymentMethod))
      })
      .catch((error) => {
        captureFunnelError(
          FunnelErrorCode.STRIPE_WALLET_AVAILABILITY_CHECK_FAILED,
          error,
          {
            level: 'warning',
            extra: {
              amount: calculatedPrice,
              countryCode,
              currency,
            },
          },
        )
      })
      .finally(() => dispatch(stopFetching(CHECK_PAYMENT_REQUEST_BUTTON)))
  }, [
    stripe,
    dispatch,
    currency,
    calculatedPrice,
    countryCode,
    availableAlternativePaymentMethods.length,
  ])
}
